import { ComponentProps, ReactNode, useEffect } from "react";
import styled from "styled-components";
import Icon from "@/atoms/Icon";
import {
  ButtonProps,
  PageSection,
  colors,
} from "@literati/public-ui-shared-react";
import CheckoutOrderSummary from "components/checkout/CheckoutOrderSummary";
import { PersonalizationCTA } from "components/checkout/ConfirmationPersonalizationCTA";
import { TestimonialCTA } from "components/checkout/ConfirmationTestimonialCTA";
import { SiteBanner, SiteBannerData } from "components/Layout/SiteBanner";

const confirmationBannerConfig: SiteBannerData = {
  line1: "<p></p>",
  line2: "",
  dismissable: false,
  // only show on the /kids/gifts page
  path_regex: "^/gifts$",
  cookie_name: "order-confirmation",
  id: 999999,
};

const Banner = styled(SiteBanner)`
  height: 50px;
  color: ${colors.tealShade3};
  background: ${colors.mintTint4};
  display: flex;
  justify-content: center;
  flex-direction: column;
  .site-banner-text {
    margin: auto;
  }
  svg {
    margin-right: 5px;
  }
`;

const PersonalizationSection = styled(PageSection)`
  padding: 4rem 0;
`;

const OrderSummary = styled(CheckoutOrderSummary)`
  margin: 5rem auto;
  width: 556px;
  max-width: calc(100% - 40px);
`;

type SharedProps = ComponentProps<typeof CheckoutOrderSummary> & {
  ctaLabel?: ReactNode;
  suppressTestimonialButton?: boolean;
  showBanner?: boolean;
};

export type SubscriptionConfirmationPageProps =
  | (SharedProps & {
      variant: "link";
      href?: string;
    })
  | (SharedProps & {
      variant: "button";
      buttonProps: ButtonProps;
    });

export const SubscriptionConfirmationPage = (
  props: SubscriptionConfirmationPageProps
) => {
  const {
    ctaLabel,
    showBanner = true,
    subscription,
    suppressTestimonialButton = false,
  } = props;
  // Reset scroll position to top of the screen since this may be modified
  // by previous checkout page.
  useEffect(() => {
    window.scrollTo(0, 0);
  });

  const sharedCtaProps = {
    ctaLabel: ctaLabel,
    subscriptionId: subscription?.id,
  };

  return (
    <>
      {showBanner ? (
        <Banner siteBanner={confirmationBannerConfig} dismissed={false}>
          <Icon name="checkmark-circle-outline" size="20px" />
          Order and payment successful!
        </Banner>
      ) : null}
      <PersonalizationSection bgColor="white">
        <PersonalizationCTA {...props} {...sharedCtaProps} />
      </PersonalizationSection>
      <PersonalizationSection bgColor="periwinkleTint5">
        <TestimonialCTA
          {...props}
          {...sharedCtaProps}
          suppressButton={suppressTestimonialButton}
        />
      </PersonalizationSection>

      <OrderSummary {...props} />
    </>
  );
};

export default SubscriptionConfirmationPage;
